import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Campaign } from '../api.js';
import Icon from '../components/Icon.js';

const STATUS_LABEL: Record<string, string> = {
  draft: 'draft', running: 'running', paused: 'paused', done: 'finished', stopped: 'stopped',
};

export default function Campaigns() {
  const nav = useNavigate();
  const [items, setItems] = useState<Campaign[]>([]);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    api.campaigns().then(setItems).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Campaigns in progress change their own progress; the list must keep up.
  useEffect(() => {
    if (!items.some((c) => c.status === 'running')) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [items, load]);

  async function act(c: Campaign, action: 'start' | 'pause' | 'stop') {
    setErr(''); setNote('');
    try { await api.campaignAction(c.id, action); load(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
  }

  async function remove(c: Campaign) {
    // Deleting takes the recordings on disk with it — the confirmation must say
    // exactly that, not a generic "are you sure?".
    if (!confirm(`Delete the campaign "${c.name}"?\n\n`
               + 'Its results and all of its audio recordings will be lost permanently.')) return;
    setErr(''); setNote('');
    try { await api.removeCampaign(c.id); setNote(`The campaign "${c.name}" was deleted.`); load(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
  }

  return (
    <>
      <div className="spread">
        <div>
          <h1>Campaigns</h1>
          <p className="subtitle">Batches of numbers tested through the SIP trunk.</p>
        </div>
        <Link className="btn primary" to="/campaigns/new"><Icon name="plus" />New campaign</Link>
      </div>

      {err && <div className="err">{err}</div>}
      {note && <div className="card" style={{ marginBottom: 14, padding: '9px 12px' }}>{note}</div>}

      {items.length === 0 ? (
        <div className="card empty">
          No campaigns yet. <Link to="/campaigns/new">Create the first one</Link>.
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Campaign</th><th>Country</th><th>CLI</th>
              <th style={{ minWidth: 150 }}>Progress</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {items.map((c) => {
                const total = Number(c.total ?? 0);
                const done = Number(c.completed ?? 0);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                const running = c.status === 'running';
                return (
                  <tr key={c.id}>
                    <td>
                      <Link to={`/campaigns/${c.id}`} style={{ fontWeight: 600 }}>{c.name}</Link>
                      <div className="muted" style={{ fontSize: 11.5 }}>
                        #{c.id} · {c.message_name} · prefix {c.allowed_prefix}
                      </div>
                    </td>
                    <td className="num">{c.country} <span className="muted">{c.lang}</span></td>
                    <td className="num">{c.cli}</td>
                    <td>
                      <div className="progress" title={`${done} of ${total}`}>
                        <div className="progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                        {done}/{total}{total > 0 && ` · ${pct}%`}
                      </div>
                    </td>
                    <td><span className={`dot ${c.status}`} />{STATUS_LABEL[c.status] ?? c.status}</td>
                    <td>
                      <div className="row-actions">
                        {running
                          ? <button className="hold icon-only" title="Pause"
                                    onClick={() => void act(c, 'pause')}><Icon name="pause" /></button>
                          : <button className="run icon-only" title="Start"
                                    onClick={() => void act(c, 'start')}><Icon name="play" /></button>}
                        <button className="icon-only" title="Stop"
                                disabled={c.status === 'draft' || c.status === 'stopped'}
                                onClick={() => void act(c, 'stop')}><Icon name="stop" /></button>
                        <button className="icon-only" title="Report"
                                onClick={() => nav(`/campaigns/${c.id}/report`)}><Icon name="report" /></button>
                        <button className="icon-only"
                                title={running ? 'Stop the campaign before editing it' : 'Edit'}
                                disabled={running}
                                onClick={() => nav(`/campaigns/${c.id}/edit`)}><Icon name="edit" /></button>
                        <button className="icon-only danger" title="Delete the campaign and its recordings"
                                disabled={running}
                                onClick={() => void remove(c)}><Icon name="trash" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
